/**
 * Akten-CSV-Export (Issue #763).
 *
 * Exportiert die aktuell gefilterte Akten-Ansicht als Excel-DE-freundliche CSV
 * (UTF-8 BOM, Semikolon-Trenner, CRLF). Rein client-seitig, kein Backend nötig.
 */

import type { Akte } from '@/lib/database.types';
import {
  serializeCsvCell,
  buildExportFilename,
  downloadBlob,
} from './forderungen-export';

// ─── Typen ────────────────────────────────────────────────

export type AktenCellValue = string | number | boolean | null | undefined;

export type AktenExportRow = Pick<
  Akte,
  | 'akten_nummer'
  | 'fahrzeug_kennzeichen'
  | 'kunde_name'
  | 'auftragsart'
  | 'status'
  | 'gesamtbetrag'
  | 'created_at'
>;

export interface AktenExportColumn {
  key: keyof AktenExportRow;
  label: string;
  width: number;
  type: 'text' | 'date' | 'number' | 'currency';
}

// ─── Spalten-Definition ───────────────────────────────────

export const aktenExportColumns: AktenExportColumn[] = [
  { key: 'akten_nummer',        label: 'Akten-Nr',     width: 20, type: 'text' },
  { key: 'fahrzeug_kennzeichen', label: 'Kennzeichen',  width: 14, type: 'text' },
  { key: 'kunde_name',          label: 'Auftraggeber', width: 24, type: 'text' },
  { key: 'auftragsart',         label: 'Auftragstyp',  width: 18, type: 'text' },
  { key: 'status',              label: 'Status',       width: 16, type: 'text' },
  { key: 'gesamtbetrag',        label: 'Gesamtbetrag', width: 14, type: 'currency' },
  { key: 'created_at',          label: 'Erstellt am',  width: 13, type: 'date' },
];

// ─── Label-Übersetzungen ──────────────────────────────────

const STATUS_LABELS: Record<string, string> = {
  neu:           'Neu',
  in_bearbeitung:'In Bearbeitung',
  warten:        'Wartend',
  abgeschlossen: 'Abgeschlossen',
  storniert:     'Storniert',
};

const AUFTRAGSART_LABELS: Record<string, string> = {
  haftpflicht:      'Haftpflicht',
  kasko:            'Kasko',
  kostenvoranschlag:'KVA',
  kurzgutachten:    'Kurzgutachten',
  beweissicherung:  'Beweissicherung',
  oldtimer:         'Oldtimer',
  wertgutachten:    'Wertgutachten',
  technisch:        'Technisch',
};

// ─── CSV ──────────────────────────────────────────────────

const BOM = '﻿';
const CRLF = '\r\n';
const DELIM = ';';

function formatAktenCsvValue(row: AktenExportRow, col: AktenExportColumn): string {
  const raw = row[col.key] as AktenCellValue;

  if (col.key === 'status' && typeof raw === 'string') {
    return serializeCsvCell(STATUS_LABELS[raw] ?? raw);
  }
  if (col.key === 'auftragsart' && typeof raw === 'string') {
    return serializeCsvCell(AUFTRAGSART_LABELS[raw] ?? raw);
  }
  if (col.type === 'date' && typeof raw === 'string' && raw !== '') {
    const d = new Date(raw);
    if (!isNaN(d.getTime())) {
      const day   = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      return `${day}.${month}.${d.getFullYear()}`;
    }
  }
  return serializeCsvCell(raw);
}

export function toCsv(
  rows: AktenExportRow[],
  columns: AktenExportColumn[],
): string {
  const header = columns.map((c) => serializeCsvCell(c.label)).join(DELIM);
  const body = rows
    .map((r) => columns.map((c) => formatAktenCsvValue(r, c)).join(DELIM))
    .join(CRLF);
  const lines = rows.length > 0 ? `${header}${CRLF}${body}${CRLF}` : `${header}${CRLF}`;
  return `${BOM}${lines}`;
}

// ─── Filename ─────────────────────────────────────────────

export function buildAktenExportFilename(now: Date = new Date()): string {
  return buildExportFilename('akten', 'csv', now, 'Europe/Berlin');
}

// ─── Browser-Download ─────────────────────────────────────

export function downloadCsv(
  rows: AktenExportRow[],
  columns: AktenExportColumn[] = aktenExportColumns,
): void {
  const csv = toCsv(rows, columns);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  downloadBlob(blob, buildAktenExportFilename());
}
